import React, {memo, forwardRef} from 'react';
import PropTypes from 'prop-types';
import {usePopoverContext} from './utils';
import {useCombinedRefs} from 'utils/react';

/**
 * PopoverTrigger - Component that triggers the popover to open/close
 * Wraps any element and makes it clickable to toggle the popover
 *
 * @typedef {Object} PopoverTriggerProps
 * @property {React.ReactNode} children - The trigger element (e.g., Button)
 * @property {boolean} [asChild] - If true, clone the child element instead of wrapping
 */
const PopoverTrigger = memo(
	forwardRef(({children, asChild = false, ...props}, forwardedRef) => {
		const {referenceRef, handleToggle, open} = usePopoverContext();

		// Always call hooks at the top level, not conditionally
		const mergedRef = useCombinedRefs(
			asChild && React.isValidElement(children) ? children.ref : null,
			referenceRef,
			forwardedRef,
		);

		if (asChild && React.isValidElement(children)) {
			// Clone the child and add our props
			// If the child can't accept a ref (function component without forwardRef),
			// React will warn, but we still need to pass it for popper positioning
			// The parent component should use forwardRef if it needs to work with refs
			return React.cloneElement(children, {
				ref: mergedRef,
				onClick: e => {
					children.props.onClick?.(e);
					handleToggle();
				},
				'aria-expanded': open,
				'aria-haspopup': 'menu',
				...props,
			});
		}

		return (
			<div ref={mergedRef} onClick={handleToggle} {...props}>
				{children}
			</div>
		);
	}),
);

PopoverTrigger.propTypes = {
	children: PropTypes.node.isRequired,
	asChild: PropTypes.bool,
};

PopoverTrigger.displayName = 'PopoverTrigger';

export default PopoverTrigger;
